import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { listLauraSAIProfiles, createLauraAudit, listProjects } from "@/app/lib/auditflowApi";
import type { LauraSAIProfile, AuditFlowProject } from "@/app/components/shared/types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { Button } from "@/components/ui/button";
import { seedLauraNhaiSampleAudit } from "./sample/seedLauraSampleAudit";

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: (auditId: string) => void;
    projectId?: string;
}

export function LauraNewAuditModal({ open, onClose, onCreated, projectId }: Props) {
    const { t } = useTranslation();
    const [title, setTitle] = useState("");
    const [profiles, setProfiles] = useState<LauraSAIProfile[]>([]);
    const [profileId, setProfileId] = useState("");
    const [language, setLanguage] = useState("pt-BR");
    const [projects, setProjects] = useState<AuditFlowProject[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [loading, setLoading] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const effectiveProjectId = projectId ?? selectedProjectId;

    useEffect(() => {
        if (!open) return;
        setError(null);
        setSelectedProjectId(projectId ?? "");
        listLauraSAIProfiles()
            .then((p) => {
                setProfiles(p);
                const tcu = p.find((x) => x.slug === "tcu");
                const def = tcu ?? p[0];
                if (def) {
                    setProfileId(def.id);
                    setLanguage(def.default_language || "pt-BR");
                }
            })
            .catch((err) => {
                setProfiles([]);
                setError(
                    err instanceof Error
                        ? err.message
                        : "Could not load SAI profiles. Run backend/migrations/20260601_add_laura_module.sql on your database.",
                );
            });

        if (!projectId) {
            listProjects()
                .then(setProjects)
                .catch(() => setProjects([]));
        }
    }, [open, projectId]);

    if (!open) return null;

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!title.trim() || !profileId || !effectiveProjectId) return;
        setLoading(true);
        setError(null);
        try {
            const audit = await createLauraAudit({
                title: title.trim(),
                sai_profile_id: profileId,
                output_language: language,
                project_id: effectiveProjectId,
            });
            onCreated(audit.id);
            onClose();
            setTitle("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create audit");
        } finally {
            setLoading(false);
        }
    }

    async function handleSampleAudit() {
        if (!profileId || !effectiveProjectId) return;
        setSeeding(true);
        setError(null);
        try {
            const auditId = await seedLauraNhaiSampleAudit({
                project_id: effectiveProjectId,
                sai_profile_id: profileId,
                output_language: language,
            });
            onCreated(auditId);
            onClose();
            setTitle("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to seed sample audit");
        } finally {
            setSeeding(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b px-5 py-4">
                    <h2 className="text-lg font-medium">New LAURA Audit</h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4 p-5">
                    {!projectId && (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                                {t("workflows", "selectProjectLabel")}
                            </label>
                            <select
                                className="w-full rounded-lg border px-3 py-2 text-sm"
                                value={selectedProjectId}
                                onChange={(e) => setSelectedProjectId(e.target.value)}
                                required
                            >
                                <option value="">{t("projects", "selectProject")}</option>
                                {projects.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                            {projects.length === 0 && (
                                <p className="mt-1 text-xs text-gray-500">
                                    {t("projects", "noProjectsFound")}
                                </p>
                            )}
                        </div>
                    )}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
                        <input
                            className="w-full rounded-lg border px-3 py-2 text-sm"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Audit title"
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">SAI Profile</label>
                        <select
                            className="w-full rounded-lg border px-3 py-2 text-sm"
                            value={profileId}
                            onChange={(e) => {
                                setProfileId(e.target.value);
                                const p = profiles.find((x) => x.id === e.target.value);
                                if (p) setLanguage(p.default_language);
                            }}
                        >
                            {profiles.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Output language</label>
                        <select
                            className="w-full rounded-lg border px-3 py-2 text-sm"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                        >
                            <option value="pt-BR">Português (Brasil)</option>
                            <option value="en">English</option>
                            <option value="es">Español</option>
                        </select>
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={seeding || !effectiveProjectId || !profileId}
                        onClick={() => void handleSampleAudit()}
                    >
                        {seeding ? (
                            <Loader2 data-icon="inline-start" className="animate-spin" />
                        ) : null}
                        {seeding ? t("laura", "seedingAudit") : t("laura", "createSampleAudit")}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        Loads NHAI toll audit data from examples (P0–P7) for testing.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || seeding || !title.trim() || !effectiveProjectId}
                            className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50"
                        >
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            Create
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
